import { useState, useEffect } from 'react';
import moment from 'moment';
import { TextField } from '@mui/material';
import ConfigPriorityTimeTask from '../../model/ConfigPriorityTimeTask';
import ConfigPriorityTimeTaskApi from '../../service/ConfigPriorityTimeTaskApi';
import TicketTypePriority from '../../enum/TicketTypePriority';

const apiConfigPriorityTimeTask = new ConfigPriorityTimeTaskApi();

export interface ServiceTimeCounterProps {
    service_time: number | null;
    service_time_start: string | null;
    service_time_end: string | null;
    returnInput?: boolean;
    noNegative?: boolean;
    ticket_type_priority?: TicketTypePriority | null;
    autoRefresh?: boolean;
}

function calcularSegundosHorarioEspecifico(dataInicial: moment.Moment, dataFinal: moment.Moment): number {
    let tempoTrabalhado: number = 0; // Em minutos

    // Verifica se a data inicial está dentro do horário de trabalho
    if (dataInicial.hours() < 8) {
        dataInicial.hours(8).minutes(0).seconds(0).milliseconds(0);
    } else if (dataInicial.hours() >= 18) {
        dataInicial.hours(17).minutes(59).seconds(59).milliseconds(999);
    }

    // Verifica se a data final está dentro do horário de trabalho
    if (dataFinal.hours() < 8) {
        dataFinal.hours(8).minutes(0).seconds(0).milliseconds(0);
    } else if (dataFinal.hours() >= 18) {
        dataFinal.hours(17).minutes(59).seconds(59).milliseconds(999);
    }

    const intervalo: number = dataFinal.diff(dataInicial, 'days');

    // Loop através de cada dia no intervalo
    for (let i = 0; i <= intervalo; i++) {
        const data = moment(dataInicial).add(i, 'days');

        // Verifica se o dia é útil (segunda a sexta-feira) e está dentro do horário especificado
        if (
            data.isoWeekday() >= 1 &&
            data.isoWeekday() <= 5 &&
            data.hours() >= 8 &&
            data.hours() < 18
        ) {
            // Calcula o tempo dentro do horário de trabalho
            const inicio = moment.max(data, dataInicial); // Hora de início do trabalho no dia
            const fim = moment.min(moment(data).hours(18).minutes(0).seconds(0).milliseconds(0), dataFinal); // Hora de término do trabalho no dia
            tempoTrabalhado += fim.diff(inicio, 'seconds');
        }
    }

    return tempoTrabalhado < 0 ? 0 : tempoTrabalhado;
}

export function formatSecondsToHoursMinutes(segundos: number): string {
    const abs = Math.abs(segundos);
    const horas = Math.floor(abs / 3600);
    const minutos = Math.floor((abs % 3600) / 60);
    const sign = segundos < 0 ? '-' : '';
    return sign + horas.toString().padStart(2, '0') + ":" + minutos.toString().padStart(2, '0');
}

export function formatSecondsToDays(segundos: number): string {
    const roundedDays = Math.round(segundos / 86400);
    if (roundedDays <= 0) {
        return '';
    }

    const days = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0, useGrouping: false })
        .format(roundedDays);
    return `${days} ${roundedDays === 1 ? 'dia' : 'dias'}`;
}

export function calculateServiceTime(start: string | null, end: string | null, currentServiceTime: number | null): { totalSeconds: number, formatedTime: string } {
    let totalTimeInSeconds = currentServiceTime || 0;

    if (end === null && start) {
        const startTime = moment(start);
        const currentTime = moment();
        totalTimeInSeconds += calcularSegundosHorarioEspecifico(startTime, currentTime);
    }

    // ceil to nearest minute so elapsed + remaining always sums to the SLA in display
    const rounded = Math.ceil(totalTimeInSeconds / 60) * 60;
    return { formatedTime: formatSecondsToHoursMinutes(rounded), totalSeconds: totalTimeInSeconds };
}

export function ServiceTimeCounter({ service_time, service_time_end, service_time_start, returnInput, autoRefresh }: ServiceTimeCounterProps) {
    const [serviceTime, setServiceTime] = useState<string>('00:00');

    useEffect(() => {
        const update = () => {
            const updatedServiceTime = calculateServiceTime(service_time_start, service_time_end, service_time);
            setServiceTime(updatedServiceTime.formatedTime);
        };
        update();
        if (!autoRefresh) return;
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [service_time_start, service_time_end, service_time, autoRefresh]);

    return (
        <>
            {returnInput ? (
                <TextField
                    name="service_time"
                    label="Tempo de atendimento"
                    margin="dense"
                    variant="outlined"
                    disabled
                    value={serviceTime}
                    fullWidth
                />
            ) : (
                serviceTime
            )}
        </>
    );
}

//Tempo de Atendimento Restante
export function RemainingServiceTimeCounter({ service_time, service_time_end, service_time_start, returnInput, noNegative, ticket_type_priority, autoRefresh }: ServiceTimeCounterProps) {
    const [remainingServiceTime, setRemainingServiceTime] = useState<string>('00:00');
    const [configPriorityTimeTask, setConfigPriorityTimeTask] = useState<ConfigPriorityTimeTask | null>(null);

    useEffect(() => {
        apiConfigPriorityTimeTask.getById(1).then(setConfigPriorityTimeTask);
    }, []);

    useEffect(() => {
        const update = () => {
            if (!configPriorityTimeTask || ticket_type_priority === null || ticket_type_priority === undefined) {
                setRemainingServiceTime('--');
                return;
            }
            const hoursMap: Record<number, number | null | undefined> = {
                [TicketTypePriority.VeryHigh]: configPriorityTimeTask.very_high,
                [TicketTypePriority.High]: configPriorityTimeTask.high,
                [TicketTypePriority.Normal]: configPriorityTimeTask.normal,
                [TicketTypePriority.Low]: configPriorityTimeTask.low,
                [TicketTypePriority.VeryLow]: configPriorityTimeTask.very_low,
            };
            const slaHours = hoursMap[ticket_type_priority] || null;
            const slaSeconds = slaHours ? slaHours * 3600 : null;
            if (slaSeconds === null) {
                setRemainingServiceTime('--');
                return;
            }
            const elapsed = calculateServiceTime(service_time_start, service_time_end, service_time);
            const elapsedCeiled = Math.ceil(elapsed.totalSeconds / 60) * 60;
            const remainingSeconds = slaSeconds - elapsedCeiled;
            if (noNegative && remainingSeconds < 0) {
                setRemainingServiceTime('SLA Violado');
            } else {
                setRemainingServiceTime(formatSecondsToHoursMinutes(remainingSeconds));
            }
        };
        update();
        if (!autoRefresh) return;
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [service_time_start, service_time_end, service_time, noNegative, ticket_type_priority, configPriorityTimeTask, autoRefresh]);

    return (
        <>
            {returnInput ? (
                <TextField
                    name="remaining_service_time"
                    label="Tempo de atendimento restante"
                    margin="dense"
                    variant="outlined"
                    disabled
                    value={remainingServiceTime}
                    fullWidth
                />
            ) : (
                remainingServiceTime
            )}
        </>
    );
}
